import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserTypesEnum } from '~/constants/enums';
import {
  useGetPackagesFaqsQuery,
  useGetPackagesQuery,
} from '~/redux/api/packagesApiSlice';
import { useGetSubscriptionPlansQuery } from '~/redux/api/subscriptionApiSlice';
import CompaniesPackages from '~/pages/Packages/CompaniesPackages';
import IndividualPackages from '~/pages/Packages/IndividualPackages';
import { buildPackagesPlans } from '~/pages/Packages/utils';
import { PackagesTabEnum } from '~/pages/Packages/enums';
import { PackageFaqType, PackageTierType } from '~/types/packagesType';
import PackagesFaqSection from '~/pages/Packages/PackagesFaqSection';
import PackageTiersModal from '~/pages/Packages/PackageTiersModal';
import '~/pages/Packages/styles.css';
import PackagesLoading from '~/pages/Packages/PackagesLoading';

type SelectedPackageTiersType = {
  tiers: PackageTierType[];
  name: string | null;
};

export default function Packages() {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const tabs: { key: PackagesTabEnum; label: string }[] = [
    { key: PackagesTabEnum.INDIVIDUAL, label: t('individuals') },
    { key: PackagesTabEnum.COMPANIES, label: t('companies') },
  ];
  const companiesUserTypes: UserTypesEnum[] = [
    UserTypesEnum.BROKER,
    UserTypesEnum.COMPOUND_DEVELOPER,
    UserTypesEnum.SMALL_DEVELOPER,
    UserTypesEnum.ALL_TYPES,
  ];
  const individualUserTypes: UserTypesEnum[] = [
    UserTypesEnum.INDIVIDUAL,
    UserTypesEnum.BUYER,
    UserTypesEnum.ALL_TYPES,
  ];
  const isCompanyUserType = (userType?: number): userType is UserTypesEnum =>
    userType !== undefined && companiesUserTypes.includes(userType);
  const isIndividualUserType = (userType?: number): userType is UserTypesEnum =>
    userType !== undefined && individualUserTypes.includes(userType);
  const [activeTab, setActiveTab] = useState<PackagesTabEnum>(
    PackagesTabEnum.INDIVIDUAL,
  );
  const [selectedPackageTiers, setSelectedPackageTiers] =
    useState<SelectedPackageTiersType>({
      tiers: [],
      name: null,
    });
  const {
    data: subscriptionPlans,
    isLoading: isSubscriptionPlansLoading,
    isUninitialized: isSubscriptionPlansUninitialized,
  } = useGetSubscriptionPlansQuery({
    locale,
  });
  const {
    data: packages,
    isLoading: isPackagesLoading,
    isUninitialized: isPackagesUninitialized,
  } = useGetPackagesQuery({
    locale,
  });
  const {
    data: faqs,
    isLoading: isFaqsLoading,
    isUninitialized: isFaqsUninitialized,
  } = useGetPackagesFaqsQuery({
    locale,
  });

  const individualsPlans = buildPackagesPlans({
    subscriptionPlans: (subscriptionPlans ?? []).filter((plan) =>
      isIndividualUserType(plan.user_type),
    ),
    packages: (packages ?? []).filter((currentPackage) =>
      isIndividualUserType(currentPackage.user_type),
    ),
  });

  const companiesPlans = buildPackagesPlans({
    subscriptionPlans: (subscriptionPlans ?? []).filter((plan) =>
      isCompanyUserType(plan.user_type),
    ),
    packages: (packages ?? []).filter((currentPackage) =>
      isCompanyUserType(currentPackage.user_type),
    ),
  });
  const individualFaqs: PackageFaqType[] = (faqs ?? []).filter((faq) =>
    individualUserTypes.includes(faq.user_type as UserTypesEnum),
  );
  const companiesFaqs: PackageFaqType[] = (faqs ?? []).filter((faq) =>
    companiesUserTypes.includes(faq.user_type as UserTypesEnum),
  );

  const isLoading =
    isSubscriptionPlansLoading ||
    isSubscriptionPlansUninitialized ||
    isPackagesLoading ||
    isPackagesUninitialized;

  useEffect(() => {
    document.title = `${t('packagesAndPrices')}|${t('aqarmap')}`;
  }, [t, locale]);

  return (
    <div className=" flex flex-col  w-full">
      <div className="flex flex-col w-full h-full items-center mb-6x md:mb-10x">
        <div className="layout-boundary pb-4x md:pb-5x pt-4x">
          <div className="layout-boundary__inner flex flex-col gap-4x md:gap-6x items-center px-2x md:px-0">
            <div className="flex flex-col gap-1.5x md:gap-2x text-center w-full content-center">
              <p className="text-title-2 md:text-title-1">
                {t('packagesAndPrices')}
              </p>
              <p className="text-body-2 md:text-body-1 text-gray__dark_2">
                {t('selectPlanDescription')}
              </p>
            </div>
            <div className="flex w-full md:w-[580px] justify-center rounded-[40px] border border-gray overflow-hidden h-full">
              {tabs.map((tab) => {
                const active = activeTab === tab.key;

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setActiveTab(tab.key);
                    }}
                    aria-selected={active}
                    className={`w-full flex-1 flex flex-nowrap justify-center items-center gap-x px-0.5x md:px-2x py-1.5x transition min-h-[48px] rounded-[40px] ${active ? 'bg-blue_light_2 ' : ''} `}
                  >
                    <div
                      className={`flex items-center gap-0.5x flex-nowrap md:text-body-1 ${
                        active
                          ? ' text-blue text-link '
                          : 'text-gray__dark_2   '
                      }`}
                    >
                      <p className="text-nowrap">{tab.label}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {isLoading ? (
          <PackagesLoading />
        ) : activeTab === PackagesTabEnum.INDIVIDUAL ? (
          <IndividualPackages
            plans={individualsPlans}
            onTiersClick={(tiers, name) =>
              setSelectedPackageTiers({ tiers, name })
            }
          />
        ) : (
          <CompaniesPackages
            plans={companiesPlans}
            onTiersClick={(tiers, name) =>
              setSelectedPackageTiers({ tiers, name })
            }
          />
        )}
      </div>
      <PackagesFaqSection
        faqs={
          activeTab === PackagesTabEnum.INDIVIDUAL
            ? individualFaqs
            : companiesFaqs
        }
        isLoading={isFaqsLoading || isFaqsUninitialized}
      />
      <PackageTiersModal
        tiers={selectedPackageTiers.tiers}
        packageName={selectedPackageTiers.name}
      />
    </div>
  );
}
